import {
  forwardRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '../../lib/cn';
import { fieldAttributes, useFieldContext } from '../FormField/context';
import { fieldShell, type FieldSize } from '../TextField/shell';

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size' | 'className'> {
  readonly size?: FieldSize;
  readonly className?: string;
  /**
   * Renders a live `6 / 10` counter beneath the control.
   *
   * The counter turns danger only AFTER a blur, never while typing: a red
   * counter on the third character of a ten-character minimum is the interface
   * telling someone off for not having finished a sentence yet.
   */
  readonly minLength?: number;
  readonly maxLength?: number;
  /**
   * Formats the counter, already localized: `(current, min) => "6 / 10"`.
   * Required whenever `minLength` or `maxLength` is set — this package holds
   * no copy.
   */
  readonly counterLabel?: (current: number, limit: number) => string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    size,
    className,
    minLength,
    maxLength,
    counterLabel,
    rows = 4,
    onChange,
    onBlur,
    value,
    defaultValue,
    ...rest
  },
  ref,
) {
  const context = useFieldContext();
  const [touched, setTouched] = useState(false);
  const [uncontrolledLength, setUncontrolledLength] = useState(
    typeof defaultValue === 'string' ? defaultValue.length : 0,
  );

  const length = typeof value === 'string' ? value.length : uncontrolledLength;
  const limit = minLength ?? maxLength;
  const short = minLength !== undefined && length < minLength;

  const wired = fieldAttributes(context, {
    ...(rest.id !== undefined ? { id: rest.id } : {}),
    ...(rest['aria-describedby'] !== undefined
      ? { 'aria-describedby': rest['aria-describedby'] }
      : {}),
    ...(rest['aria-invalid'] !== undefined
      ? { 'aria-invalid': rest['aria-invalid'] === true || rest['aria-invalid'] === 'true' }
      : {}),
    ...(rest.required !== undefined ? { required: rest.required } : {}),
  });

  function handleChange(event: ChangeEvent<HTMLTextAreaElement>): void {
    if (typeof value !== 'string') setUncontrolledLength(event.target.value.length);
    onChange?.(event);
  }

  function handleBlur(event: FocusEvent<HTMLTextAreaElement>): void {
    setTouched(true);
    onBlur?.(event);
  }

  return (
    <div className="flex flex-col gap-1">
      <textarea
        {...rest}
        {...wired}
        ref={ref}
        rows={rows}
        {...(typeof value === 'string' ? { value } : {})}
        {...(typeof defaultValue === 'string' ? { defaultValue } : {})}
        {...(maxLength !== undefined ? { maxLength } : {})}
        {...(minLength !== undefined ? { minLength } : {})}
        onChange={handleChange}
        onBlur={handleBlur}
        className={cn(fieldShell({ size }), 'resize-y py-2 leading-normal', className)}
      />
      {limit !== undefined && counterLabel !== undefined ? (
        <p
          // Announced only on change, and only once the user has left the
          // field — a counter that shouts on every keystroke is unusable.
          aria-live="polite"
          className={cn(
            'num self-end text-support tabular-nums',
            touched && short ? 'text-status-danger-ink' : 'text-ink-muted',
          )}
        >
          {counterLabel(length, limit)}
        </p>
      ) : null}
    </div>
  );
});
